<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <div
    class="grid-container-editor"
    :style="{
      gridTemplateColumns: `repeat(${gridSettings.columns}, 1fr)`,
      gridTemplateRows: `repeat(${gridSettings.rows}, 100px)`,
    }"
    @dragover="$emit('dragover', $event)"
    @dragleave="$emit('dragleave', $event)"
    @drop="$emit('drop', $event)"
  >
    <!-- Grid overlay showing cells -->
    <div
      v-for="n in gridSettings.columns * gridSettings.rows"
      :key="`cell-${n}`"
      class="grid-cell"
      :data-cell="n"
      :style="getCellStyle(n)"
    />

    <!-- Drag preview overlay -->
    <div
      v-if="dragPreview"
      class="drag-preview"
      :style="{
        gridColumn: `${dragPreview.colStart} / ${dragPreview.colEnd}`,
        gridRow: `${dragPreview.rowStart} / ${dragPreview.rowEnd}`,
      }"
    >
      <div class="drag-preview-content">
        <i class="ri-drag-move-2-line drag-preview-icon"></i>
        <span class="drag-preview-text">{{
          $t("dashboardAdmin.dropHere")
        }}</span>
      </div>
    </div>

    <!-- Blocks -->
    <GridBlock
      v-for="block in blocks"
      :key="block.id"
      :block="block"
      :grid-settings="gridSettings"
      :is-selected="selectedBlockId === block.id"
      :has-overlap="overlappingBlockIds.includes(block.id)"
      @dragstart="$emit('dragstart', $event, block)"
      @dragend="$emit('dragend', $event)"
      @click="$emit('select-block', block)"
      @move-to-basket="$emit('move-to-basket', block)"
      @duplicate="$emit('duplicate-block', block)"
      @edit="$emit('edit-block', block)"
      @delete="$emit('delete-block', block)"
      @resize-start="(...args) => $emit('resize-start', ...args)"
    />
  </div>
</template>

<script>
import GridBlock from "@/components/dashboard/editor/grid/GridBlock.vue";

export default {
  name: "GridEditor",
  components: {
    GridBlock,
  },
  props: {
    gridSettings: {
      type: Object,
      required: true,
    },
    blocks: {
      type: Array,
      required: true,
      default: () => [],
    },
    dragPreview: {
      type: Object,
      default: null,
    },
    selectedBlockId: {
      type: String,
      default: null,
    },
    overlappingBlockIds: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    "dragstart",
    "dragend",
    "dragover",
    "dragleave",
    "drop",
    "select-block",
    "edit-block",
    "duplicate-block",
    "delete-block",
    "move-to-basket",
    "resize-start",
  ],
  methods: {
    getCellStyle(cellNumber) {
      // Calculate the exact grid position for each overlay cell
      const col = ((cellNumber - 1) % this.gridSettings.columns) + 1;
      const row = Math.floor((cellNumber - 1) / this.gridSettings.columns) + 1;
      return {
        gridColumn: `${col} / ${col + 1}`,
        gridRow: `${row} / ${row + 1}`,
      };
    },
  },
};
</script>

<style scoped>
.grid-container-editor {
  display: grid;
  gap: 1rem;
  position: relative;
  background: var(--p-content-background);
  padding: 1rem;
  border-radius: 0.5rem;
  border: 2px dashed var(--p-content-border-color);
  grid-auto-rows: 0;
  overflow: hidden;
}

.grid-cell {
  border: 1px dashed var(--p-content-border-color);
  box-sizing: border-box;
  pointer-events: none;
}

.drag-preview {
  background: var(--p-primary-100);
  border: 2px dashed var(--p-primary-500);
  border-radius: 0.5rem;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  animation: pulse 1.5s ease-in-out infinite;
}

.drag-preview-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: var(--p-primary-700);
}

.drag-preview-icon {
  font-size: 1.5rem;
}

.drag-preview-text {
  font-size: 0.875rem;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

/* Ensure blocks are above overlay */
.block-editor {
  z-index: 2;
}
</style>
